import * as React from 'react'
import { NavLink } from 'react-router-dom'

import { AccessLock } from '@/components/AccessLock'
import {
  SIDEBAR_FLYOUT_ANCHOR_OFFSET_PX,
  SIDEBAR_FLYOUT_VIEWPORT_MARGIN_PX,
} from '@/config/constants'
import { cn } from '@/lib/utils'
import type { NavCategory } from './AppSidebarNav'

interface AppSidebarFlyoutProps {
  /** Id the category button's `aria-controls` points at. */
  id: string
  /** Owned by AppSidebar: dismissal containment needs the panel's node. */
  panelRef: React.RefObject<HTMLDivElement | null>
  category: NavCategory
  /** The rail button this flyout is anchored to, for vertical placement. */
  anchorEl: HTMLElement | null
  /** Set when the flyout was opened by click or keyboard rather than hover. */
  focusFirstRow: boolean
  onMouseEnter: () => void
  onMouseLeave: () => void
  /** Fires on row click/activation, so AppSidebar can close the flyout. */
  onNavigate: () => void
}

const ROW_CLASS = cn(
  'flex min-h-10 w-full items-center gap-2 rounded-lg p-2',
  'text-[14px] font-medium whitespace-nowrap text-primary-high-contrast',
  'hover:bg-primary-high-contrast/[0.07]',
  '[&.active]:bg-primary-ui-bg [&[aria-current=page]]:bg-primary-ui-bg',
  '[&.active]:text-primary-solid [&[aria-current=page]]:text-primary-solid',
  '[&.active_svg]:text-primary-solid [&[aria-current=page]_svg]:text-primary-solid',
  'focus-visible:outline-2 focus-visible:outline-primary-solid focus-visible:-outline-offset-2'
)

/**
 * The panel's `top`, clamped into the viewport and kept on its anchor.
 *
 * A hook rather than an inline effect because the measurement subscribes to
 * two external systems: the rail scrolls on short viewports, so the anchor can
 * move with no re-render to notice it, and a window resize changes both the
 * anchor's position and the clamp. Keyed on `categoryId` — a stable string —
 * rather than the category object, whose identity is not guaranteed.
 */
function useFlyoutPosition(
  anchorEl: HTMLElement | null,
  panelRef: React.RefObject<HTMLDivElement | null>,
  categoryId: string
) {
  const [top, setTop] = React.useState(SIDEBAR_FLYOUT_VIEWPORT_MARGIN_PX)

  const measure = React.useCallback(() => {
    const panel = panelRef.current
    if (!anchorEl || !panel) return
    const preferred =
      anchorEl.getBoundingClientRect().top - SIDEBAR_FLYOUT_ANCHOR_OFFSET_PX
    const maxTop =
      window.innerHeight -
      panel.offsetHeight -
      SIDEBAR_FLYOUT_VIEWPORT_MARGIN_PX
    setTop(
      Math.max(SIDEBAR_FLYOUT_VIEWPORT_MARGIN_PX, Math.min(preferred, maxTop))
    )
  }, [anchorEl, panelRef])

  React.useLayoutEffect(() => {
    measure()
    const rail = anchorEl?.closest('[data-slot="sidebar"]')
    window.addEventListener('resize', measure)
    rail?.addEventListener('scroll', measure)
    return () => {
      window.removeEventListener('resize', measure)
      rail?.removeEventListener('scroll', measure)
    }
  }, [measure, anchorEl, categoryId])

  return top
}

/**
 * The 200px panel a rail category opens to its right.
 *
 * Presentational only — AppSidebar owns which category is open, when it
 * opens/closes, and every keyboard/pointer rule around that; this component
 * just measures its anchor and renders the category's rows.
 *
 * Rendered inline in AppSidebar's own tree rather than portaled, so it sits
 * between the category buttons and the footer controls in DOM order and Tab
 * moves through it where the eye expects. A body portal would put it after
 * the footer in tab order.
 */
export function AppSidebarFlyout({
  id,
  panelRef,
  category,
  anchorEl,
  focusFirstRow,
  onMouseEnter,
  onMouseLeave,
  onNavigate,
}: AppSidebarFlyoutProps) {
  const firstRowRef = React.useRef<HTMLAnchorElement>(null)
  const titleId = React.useId()
  const top = useFlyoutPosition(anchorEl, panelRef, category.id)

  // Only when the rail says this open came from a click or a key — a hover
  // open must not yank focus out from under the pointer. Two guards against
  // stealing focus the user has since moved: the dependency is the category's
  // id, not the object (whose identity used to change every render), and a
  // focus already inside the panel is left where it is.
  React.useLayoutEffect(() => {
    if (!focusFirstRow) return
    if (panelRef.current?.contains(document.activeElement)) return
    firstRowRef.current?.focus()
    // panelRef is a stable ref object owned by AppSidebar; listing it keeps
    // exhaustive-deps happy without causing a re-run.
  }, [focusFirstRow, category.id, panelRef])

  return (
    <div
      ref={panelRef}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      // maxHeight is inline rather than a Tailwind `max-h-[calc(100vh-24px)]`
      // so the viewport margin has one definition: the literal would quietly
      // desync from the constant useFlyoutPosition clamps against.
      style={{
        top,
        maxHeight: `calc(100vh - ${SIDEBAR_FLYOUT_VIEWPORT_MARGIN_PX * 2}px)`,
      }}
      className={cn(
        'fixed left-[var(--app-sidebar-width)] z-40 w-[200px]',
        'overflow-y-auto',
        'rounded-xl bg-app shadow-[0_0_8px_rgba(0,0,0,0.10)]',
        // Invisible bridge on the left edge so the pointer can cross the gap
        // to the rail without the mouseleave close-timer firing. No `relative`
        // here: `fixed` above already gives the pseudo its containing block,
        // and cn()'s tailwind-merge would drop `fixed` as the losing half of
        // the position conflict, putting the panel back in normal flow.
        "before:absolute before:inset-y-0 before:right-full before:w-3.5 before:content-['']"
      )}>
      <div
        id={titleId}
        className="px-3 pt-3 text-[14px] font-medium text-primary-high-contrast/45">
        {category.label}
      </div>
      <nav id={id} className="flex flex-col p-3" aria-labelledby={titleId}>
        {category.items.map((item, index) => {
          const Icon = item.icon
          if (item.external) {
            return (
              <a
                key={item.id}
                ref={index === 0 ? firstRowRef : undefined}
                href={item.to}
                className={ROW_CLASS}
                onClick={onNavigate}>
                <Icon size={16} strokeWidth={1.5} />
                <span>{item.label}</span>
                {item.locked && <AccessLock className="ml-auto" />}
              </a>
            )
          }
          return (
            <NavLink
              key={item.id}
              ref={index === 0 ? firstRowRef : undefined}
              to={item.to}
              end={item.end}
              className={ROW_CLASS}
              onClick={onNavigate}>
              <Icon size={16} strokeWidth={1.5} />
              <span>{item.label}</span>
              {item.locked && <AccessLock className="ml-auto" />}
            </NavLink>
          )
        })}
      </nav>
    </div>
  )
}
